import React, { ReactNode } from 'react';

/**
 * Maps over the children of a component, recursively, and returns a flat array
 * of string versions of the leaf-children. Nodes representing Components that
 * do not have children are replaced with the component's display name. All
 * nodes with children are mapped over recursively, and do not appear in the
 * final array.
 *
 * This function is useful for generating test ids and labels for components
 * or any debug-value that is derived from the children of a component.
 *
 * Copied from @waybetter/react
 *
 * @param {ReactNode?} children
 * @returns {string[]}
 */
export function extractTextChildren(children) {
  return React.Children.toArray(children)
    .flatMap((child) => {
      const maybeChildren = child?.props?.children;
      return maybeChildren != null
        ? extractTextChildren(maybeChildren)
        : [child];
    })
    .map((child) => {
      const { type } = child || {};
      if (type) {
        return typeof type === 'string'
          ? type
          : `[${type?.displayName || type?.name || 'Unknown'}]`;
      }

      return child?.toString();
    });
}

/**
 * Generates a string label for a component based on its children.
 * Children are extracted from the component, flattened into a single array,
 * and then joined together with a space.
 *
 * Copied from @waybetter/react
 *
 * @param {ReactNode?} children
 * @returns {string}
 */
export function generateLabel(children) {
  return extractTextChildren(children).join(' ').replace(/\s+/g, ' ');
}
